import { InfoOutlined } from '@mui/icons-material'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2 as Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Paper from 'components/@extended/Paper'
import AcceleratorSelector from 'components/AcceleratorSelector'
import ComponentDisplay from 'components/ComponentDisplay'
import FormulaEditor from 'components/FormulaEditor'
import React from 'react'
import type { VariableAllocationRow } from 'types/comp-allocations'
import type { ExpandedCompensationPlan } from 'types/comp-plan'
import type { Component, ExpandedTarget, TargetDTO } from 'types/components'
import { getCompositeFunction } from 'types/components'
import { createIdLabelMap } from 'types/periods'

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

interface PlanAllocationTablesProps {
  plan: ExpandedCompensationPlan
}

const PlanAllocationsCard: React.FC<PlanAllocationTablesProps> = ({ plan }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [selectedRow, setSelectedRow] = React.useState<{
    row: VariableAllocationRow
    groupLabel: string
    periodLabel: string
  } | null>(null)

  const baseSalary = plan.plan.baseSalary
  const variablePercent = plan.plan.variablePercent
  const allocations = plan.plan.variableAllocations
  const periodProration = plan.plan.periodProration

  const expandedTargets = plan.targets.map((target) => {
    const component = plan.components.find((component) => component.id === target.componentId)
    return {
      target,
      component,
    } as ExpandedTarget
  })

  const componentsMap = plan.components.reduce((acc: Record<string, Component>, component) => {
    if (component.id != null) {
      acc[component.id] = component
    }
    return acc
  }, {})

  const targetsMap = plan.targets.reduce((acc: Record<string, TargetDTO>, target) => {
    const key = `${target.componentId}_${target.periodId}`
    acc[key] = target
    return acc
  }, {})

  const periodIdLabelMap = createIdLabelMap(plan.period)

  // Flatten all rows for table view
  const allRows = allocations.flatMap((table) =>
    table.rows
      .filter((row) => !row.disabled)
      .map((row) => ({
        row,
        groupLabel: table.label,
        periodLabel: periodIdLabelMap[row.periodId as string] || '',
      }))
  )

  const handleViewDetails = (row: VariableAllocationRow, groupLabel: string, periodLabel: string) => {
    setSelectedRow({ row, groupLabel, periodLabel })
  }

  const rowTargets = selectedRow?.row
    ? expandedTargets?.filter((target) => selectedRow.row.componentIds?.includes(target.target.componentId)) || []
    : []

  return (
    <>
      {isMobile ? (
        // Mobile: Tile view
        <Stack spacing={2}>
          {allRows.map((item, index) => {
            const allocationPercent = ((item.row.allocationPercentage ?? 0) * 100).toFixed(2)
            const onTargetPayout = baseSalary * item.row.allocationPercentage * variablePercent * periodProration

            return (
              <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {item.row.label}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleViewDetails(item.row, item.groupLabel, item.periodLabel)}
                    sx={{ color: 'primary.main' }}
                  >
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Group
                    </Typography>
                    <Typography variant="body2">{item.groupLabel}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Period
                    </Typography>
                    <Typography variant="body2">{item.periodLabel}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Allocation %
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {allocationPercent}%
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      On-Target
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(onTargetPayout)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )
          })}
        </Stack>
      ) : (
        // Desktop: Table view
        <Paper>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
            <Table
              stickyHeader
              size="small"
              aria-label="allocations-table"
              sx={{
                width: '100%',
                tableLayout: 'fixed',
                '& .MuiTableCell-stickyHeader': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                },
              }}
            >
              <TableHead
                sx={{
                  '& th': {
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `2px solid ${theme.palette.divider} !important`,
                  },
                }}
              >
                <TableRow>
                  <TableCell sx={{ minWidth: 150 }}>
                    <strong>Allocation</strong>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <strong>Group</strong>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <strong>Period</strong>
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }} align="right">
                    <strong>Allocation %</strong>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }} align="right">
                    <strong>On-Target</strong>
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }} align="center">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allRows.map((item, index) => {
                  const allocationPercent = ((item.row.allocationPercentage ?? 0) * 100).toFixed(2)
                  const onTargetPayout = baseSalary * item.row.allocationPercentage * variablePercent * periodProration

                  return (
                    <TableRow key={index} sx={{ py: 3 }}>
                      <TableCell>{item.row.label}</TableCell>
                      <TableCell>{item.groupLabel}</TableCell>
                      <TableCell>{item.periodLabel}</TableCell>
                      <TableCell align="right">{allocationPercent}%</TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(onTargetPayout)}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(item.row, item.groupLabel, item.periodLabel)}
                          sx={{ color: 'primary.main' }}
                        >
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Details Dialog for Table View */}
      {selectedRow && (
        <Dialog open={!!selectedRow} onClose={() => setSelectedRow(null)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Typography variant="h5" fontWeight="bold">
              {selectedRow.row.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {selectedRow.groupLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {selectedRow.periodLabel}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3}>
              {/* Summary Info */}
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Allocation
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {((selectedRow.row.allocationPercentage ?? 0) * 100).toFixed(2)}%
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  On-Target
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {formatCurrency(
                    baseSalary * selectedRow.row.allocationPercentage * variablePercent * periodProration
                  )}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Max Payout
                </Typography>
                <Typography variant="body1">{(selectedRow.row.maxPayout * 100).toFixed(0)}%</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Accelerator
                </Typography>
                <AcceleratorSelector
                  value={selectedRow.row.achievementMultiplier}
                  setValue={() => {}}
                  incrementAmount={0.1}
                  reviewOnly
                  fontSize="0.875rem"
                />
              </Grid>

              {/* Targets Section */}
              {rowTargets.length > 0 && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                      Targets
                    </Typography>
                  </Grid>
                  {rowTargets.map((expandedTarget, idx) => {
                    const component = componentsMap[expandedTarget.target.componentId]
                    const target = targetsMap[`${expandedTarget.target.componentId}_${expandedTarget.target.periodId}`]

                    if (!component || !target) return null

                    return (
                      <Grid key={idx} size={{ xs: 12 }}>
                        <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Target
                              </Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {component.label}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Value
                              </Typography>
                              <Typography variant="body1">
                                <ComponentDisplay
                                  componentType={component.componentType}
                                  value={target.value || '0'}
                                  sourceType={component.sourceType}
                                  compositeFunction={getCompositeFunction(component.sourceInfo)}
                                  componentLabel={component.label}
                                  periodLabel={selectedRow.periodLabel}
                                  isTarget={true}
                                  fontSize="inherit"
                                />
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    )
                  })}
                </>
              )}

              {/* Formula Editor */}
              {rowTargets.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    Formula
                  </Typography>
                  <FormulaEditor
                    handleFormulaChange={() => {}}
                    selectedTargets={rowTargets}
                    currentRequirements={selectedRow.row.formulaRequirements}
                    currentOptions={selectedRow.row.formulaOptions}
                    theme={theme}
                    rowType={selectedRow.row.rowType}
                    isReviewMode
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default PlanAllocationsCard
